/**
 * Top-down 2D preview of a recorded shot, drawn to a canvas: the pre-shot ball
 * layout plus the cue ball's route path. The route is derived by re-simulating
 * the stored cue on a world rebuilt from the shot's positions + physics — the
 * same deterministic solve the live game uses — so the preview matches the real
 * shot exactly. Used both for the small list thumbnails and the enlarged preview.
 */
import { tableFor, ballsFor, ballRadius, BALL_COLOR, type BallId, type VariantId, type PhysicsConstants } from '../config';
import { createCaromWorld, simulate } from '../physics';
import type { ShotRecord } from '../net/protocol';

const hex = (n: number): string => '#' + (n & 0xffffff).toString(16).padStart(6, '0');

/** Rebuild the shot's world (positions from the record) and return the cue-ball path. */
function cueRoute(
  record: ShotRecord,
  variant: VariantId,
  physics: PhysicsConstants
): { x: number; y: number }[] {
  const world = createCaromWorld(variant, physics);
  for (const id of ballsFor(variant)) {
    const b = world.get(id);
    const p = record.positions[id];
    if (p) b.pos = { x: p.x, y: p.y, z: b.pos.z };
    b.vel = { x: 0, y: 0, z: 0 };
    b.angVel = { x: 0, y: 0, z: 0 };
    b.state = 'stationary';
  }
  const shot = simulate(world, record.cue);
  const cueId = record.cue.ball;
  return shot.frames.map((f) => ({ x: f.balls[cueId].pos.x, y: f.balls[cueId].pos.y }));
}

export interface ThumbnailOptions {
  /** Draw the cue route polyline (default true). */
  route?: boolean;
  /** Cloth colour (css); default a muted green. */
  cloth?: string;
}

/**
 * Render `record` into `canvas`. The table's long axis (length/y) runs
 * horizontally. Sizes to the canvas's backing-store resolution, so set
 * `canvas.width`/`canvas.height` before calling (retina: multiply by DPR).
 */
export function drawShotThumbnail(
  canvas: HTMLCanvasElement,
  record: ShotRecord,
  variant: VariantId,
  physics: PhysicsConstants,
  opts: ThumbnailOptions = {}
): void {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  const W = canvas.width;
  const H = canvas.height;
  ctx.clearRect(0, 0, W, H);

  const table = tableFor(variant);
  const halfW = table.playWidth / 2; // table x extent
  const halfL = table.playLength / 2; // table y extent (longer)
  const r = ballRadius(variant);

  // fit the (length × width) bed into the canvas with a small margin, long axis
  // horizontal → canvasX ← table y, canvasY ← table x.
  const pad = Math.max(6, Math.min(W, H) * 0.06);
  const bedW = 2 * halfL;
  const bedH = 2 * halfW;
  const s = Math.min((W - 2 * pad) / bedW, (H - 2 * pad) / bedH);
  const ox = (W - bedW * s) / 2;
  const oy = (H - bedH * s) / 2;
  const px = (ty: number): number => ox + (ty + halfL) * s; // table y → canvas x
  const py = (tx: number): number => oy + (tx + halfW) * s; // table x → canvas y

  // cloth + rail frame
  ctx.fillStyle = opts.cloth ?? '#1f6b45';
  roundRect(ctx, ox, oy, bedW * s, bedH * s, Math.min(6, 4 * s * r));
  ctx.fill();
  ctx.strokeStyle = 'rgba(120,72,40,0.9)';
  ctx.lineWidth = Math.max(2, pad * 0.5);
  ctx.stroke();

  // cue route
  if (opts.route !== false) {
    const route = cueRoute(record, variant, physics);
    if (route.length > 1) {
      ctx.beginPath();
      ctx.moveTo(px(route[0].y), py(route[0].x));
      for (let i = 1; i < route.length; i++) ctx.lineTo(px(route[i].y), py(route[i].x));
      ctx.strokeStyle = 'rgba(255,255,255,0.85)';
      ctx.lineWidth = Math.max(1.2, s * r * 0.5);
      ctx.lineJoin = 'round';
      ctx.stroke();
    }
  }

  // balls at their pre-shot positions
  const ballR = Math.max(2.5, s * r);
  for (const id of ballsFor(variant)) {
    const p = record.positions[id];
    if (!p) continue;
    ctx.beginPath();
    ctx.arc(px(p.y), py(p.x), ballR, 0, Math.PI * 2);
    ctx.fillStyle = hex(BALL_COLOR[id]);
    ctx.fill();
    ctx.strokeStyle = 'rgba(0,0,0,0.35)';
    ctx.lineWidth = 1;
    ctx.stroke();
    // ring the cue ball so it stands out
    if (id === record.cue.ball) {
      ctx.beginPath();
      ctx.arc(px(p.y), py(p.x), ballR + 2, 0, Math.PI * 2);
      ctx.strokeStyle = 'rgba(30,120,255,0.95)';
      ctx.lineWidth = 1.5;
      ctx.stroke();
    }
  }
}

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  const rr = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.arcTo(x + w, y, x + w, y + h, rr);
  ctx.arcTo(x + w, y + h, x, y + h, rr);
  ctx.arcTo(x, y + h, x, y, rr);
  ctx.arcTo(x, y, x + w, y, rr);
  ctx.closePath();
}
